/**
 * Utility functions for form validation and error handling
 */

/**
 * Scrolls to the first error field on the page
 * @param errors - Object containing field errors
 * @param delay - Delay before scrolling (default: 100ms)
 */
export const scrollToFirstError = (errors: { [key: string]: string }, delay: number = 100) => {
  if (!errors || Object.keys(errors).length === 0) {
    return
  }

  // Get the first error field name
  const firstErrorField = Object.keys(errors)[0]
  
  // Try multiple selectors to find the error field
  const selectors = [
    // Direct name attribute
    `[name="${firstErrorField}"]`,
    // Radio buttons with the field name
    `input[type="radio"][name="${firstErrorField}"]`,
    // Checkboxes with the field name
    `input[type="checkbox"][name="${firstErrorField}"]`,
    // ID selector
    `#${firstErrorField}`,
    // Data attribute
    `[data-field="${firstErrorField}"]`,
    // Custom error field class
    `.error-field-${firstErrorField}`,
    // Look for elements with error styling (more specific)
    `.border-red-500[name="${firstErrorField}"]`,
    // Look for error text elements
    `.text-red-600:contains("${firstErrorField}")`,
    // Fallback: any element with error styling
    '.border-red-500',
    '.text-red-600'
  ]

  setTimeout(() => {
    let targetElement: HTMLElement | null = null

    // Try each selector until we find an element
    for (const selector of selectors) {
      try {
        targetElement = document.querySelector(selector)
        if (targetElement) {
          break
        }
      } catch (e) {
        // Skip invalid selectors
        continue
      }
    }

    // If we found an element, scroll to it
    if (targetElement) {
      // For radio buttons and checkboxes, try to find the container or label
      let scrollTarget = targetElement
      
      // If it's a radio button or checkbox, try to find the parent container
      if (targetElement instanceof HTMLInputElement && 
          (targetElement.type === 'radio' || targetElement.type === 'checkbox')) {
        // Look for the parent fieldset, div with specific classes, or the label
        const parentContainer = targetElement.closest('fieldset') || 
                               targetElement.closest('.space-y-4') ||
                               targetElement.closest('.space-y-3') ||
                               targetElement.closest('.space-y-2') ||
                               targetElement.closest('div')
        
        if (parentContainer) {
          scrollTarget = parentContainer as HTMLElement
        }
      }

      // Scroll to the element with some offset from the top
      const elementRect = scrollTarget.getBoundingClientRect()
      const absoluteElementTop = elementRect.top + window.pageYOffset
      const offset = 100 // Offset from top of viewport
      
      window.scrollTo({
        top: absoluteElementTop - offset,
        behavior: 'smooth'
      })

      // Focus the element if it's focusable
      if (targetElement instanceof HTMLInputElement || 
          targetElement instanceof HTMLSelectElement || 
          targetElement instanceof HTMLTextAreaElement) {
        targetElement.focus()
      }
    } else {
      // Fallback: scroll to top of page if no specific element found
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
  }, delay)
}

/**
 * Highlights error fields by adding a temporary class
 * @param errors - Object containing field errors
 */
export const highlightErrorFields = (errors: { [key: string]: string }) => {
  if (!errors || Object.keys(errors).length === 0) {
    return
  }

  // Remove any existing error highlights
  document.querySelectorAll('.error-highlight').forEach(el => {
    el.classList.remove('error-highlight')
  })

  // Add highlight to error fields
  Object.keys(errors).forEach(fieldName => {
    const selectors = [
      `[name="${fieldName}"]`,
      `#${fieldName}`,
      `[data-field="${fieldName}"]`
    ]

    selectors.forEach(selector => {
      const element = document.querySelector(selector)
      if (element) {
        element.classList.add('error-highlight')
        // Remove highlight after 3 seconds
        setTimeout(() => {
          element.classList.remove('error-highlight')
        }, 3000)
      }
    })
  })
}

/**
 * Combined function to scroll to first error and highlight all error fields
 * @param errors - Object containing field errors
 * @param delay - Delay before scrolling (default: 100ms)
 */
export const handleValidationErrors = (errors: { [key: string]: string }, delay: number = 100) => {
  scrollToFirstError(errors, delay)
  highlightErrorFields(errors)
}
